import React, { useState, Component } from 'react';
import { UsersIcon, BookOpenIcon, StarIcon, SendIcon, TrendingUpIcon, SearchIcon, CheckCircleIcon, AlertCircleIcon, XCircleIcon, BellIcon, EyeIcon, TrashIcon, ChevronDownIcon, MoreVerticalIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
// --- Types ---
type ARCStatus = 'sent' | 'reading' | 'reviewed' | 'overdue' | 'declined';
interface ARCReader {
  id: string;
  name: string;
  email: string;
  initials: string;
  avatarColor: string;
  bookTitle: string;
  bookCover: string;
  status: ARCStatus;
  dateSent: string;
  dueDate: string;
  rating?: number;
  isOverdue?: boolean;
}
// --- Mock Data ---
const mockReaders: ARCReader[] = [{
  id: 'ARC-001',
  name: 'Sarah Johnson',
  email: 'sarah.j@email.com',
  initials: 'SJ',
  avatarColor: 'bg-purple-500',
  bookTitle: 'The Silent Echo',
  bookCover: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  status: 'reviewed',
  dateSent: 'Oct 01, 2023',
  dueDate: 'Oct 21, 2023',
  rating: 5
}, {
  id: 'ARC-002',
  name: 'Mike Brown',
  email: 'mike.b@email.com',
  initials: 'MB',
  avatarColor: 'bg-blue-500',
  bookTitle: 'The Silent Echo',
  bookCover: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  status: 'reviewed',
  dateSent: 'Oct 03, 2023',
  dueDate: 'Oct 23, 2023',
  rating: 4
}, {
  id: 'ARC-003',
  name: 'Emily Davis',
  email: 'emily.d@email.com',
  initials: 'ED',
  avatarColor: 'bg-teal-500',
  bookTitle: 'The Silent Echo',
  bookCover: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  status: 'reading',
  dateSent: 'Oct 10, 2023',
  dueDate: 'Oct 30, 2023'
}, {
  id: 'ARC-004',
  name: 'James Wilson',
  email: 'james.w@email.com',
  initials: 'JW',
  avatarColor: 'bg-amber-500',
  bookTitle: 'The Silent Echo',
  bookCover: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  status: 'overdue',
  dateSent: 'Oct 15, 2023',
  dueDate: 'Nov 04, 2023',
  isOverdue: true
}, {
  id: 'ARC-005',
  name: 'Lisa Chen',
  email: 'lisa.c@email.com',
  initials: 'LC',
  avatarColor: 'bg-pink-500',
  bookTitle: 'Whispers in the Dark',
  bookCover: 'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  status: 'reviewed',
  dateSent: 'Sep 20, 2023',
  dueDate: 'Oct 10, 2023',
  rating: 5
}, {
  id: 'ARC-006',
  name: 'David Park',
  email: 'david.p@email.com',
  initials: 'DP',
  avatarColor: 'bg-indigo-500',
  bookTitle: 'Whispers in the Dark',
  bookCover: 'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  status: 'reading',
  dateSent: 'Oct 08, 2023',
  dueDate: 'Oct 28, 2023'
}, {
  id: 'ARC-007',
  name: 'Rachel Kim',
  email: 'rachel.k@email.com',
  initials: 'RK',
  avatarColor: 'bg-red-400',
  bookTitle: 'Whispers in the Dark',
  bookCover: 'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  status: 'overdue',
  dateSent: 'Sep 25, 2023',
  dueDate: 'Oct 15, 2023',
  isOverdue: true
}, {
  id: 'ARC-008',
  name: 'Tom Martinez',
  email: 'tom.m@email.com',
  initials: 'TM',
  avatarColor: 'bg-green-500',
  bookTitle: 'Beyond the Veil',
  bookCover: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  status: 'reviewed',
  dateSent: 'Sep 15, 2023',
  dueDate: 'Oct 05, 2023',
  rating: 4
}, {
  id: 'ARC-009',
  name: 'Anna White',
  email: 'anna.w@email.com',
  initials: 'AW',
  avatarColor: 'bg-gray-500',
  bookTitle: 'Beyond the Veil',
  bookCover: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  status: 'declined',
  dateSent: 'Oct 05, 2023',
  dueDate: 'Oct 25, 2023'
}, {
  id: 'ARC-010',
  name: 'Chris Lee',
  email: 'chris.l@email.com',
  initials: 'CL',
  avatarColor: 'bg-orange-500',
  bookTitle: 'Beyond the Veil',
  bookCover: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  status: 'reading',
  dateSent: 'Oct 12, 2023',
  dueDate: 'Nov 01, 2023'
}];
// --- Helper Components ---
const StatusBadge = ({
  status
}: {
  status: ARCStatus;
}) => {
  const styles = {
    sent: 'bg-gray-100 text-gray-800 border-gray-200',
    reading: 'bg-blue-100 text-blue-800 border-blue-200',
    reviewed: 'bg-green-100 text-green-800 border-green-200',
    overdue: 'bg-red-100 text-red-800 border-red-200',
    declined: 'bg-gray-200 text-gray-600 border-gray-300'
  };
  const labels = {
    sent: 'Sent',
    reading: 'Reading',
    reviewed: 'Reviewed',
    overdue: 'Overdue',
    declined: 'Declined'
  };
  const rt = useRoleTheme();
  return <span className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium border ${styles[status]}`} data-id="element-17770" data-mp="3t83hyvq0prl2">
      {status === 'sent' && <SendIcon className="w-3 h-3 mr-1" data-id="element-17771" data-mp="3kguvojh8m32y" />}
      {status === 'reading' && <BookOpenIcon className="w-3 h-3 mr-1" data-id="element-17772" data-mp="3kei37cdusabn" />}
      {status === 'reviewed' && <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-17773" data-mp="3fi9qs6r1iv86" />}
      {status === 'overdue' && <AlertCircleIcon className="w-3 h-3 mr-1" data-id="element-17774" data-mp="3aj6ogu2g333w" />}
      {status === 'declined' && <XCircleIcon className="w-3 h-3 mr-1" data-id="element-17775" data-mp="35j2ihxz5xqrx" />}
      {labels[status]}
    </span>;
};
const ARCManagement = () => {
  const dk = useDarkClasses();
  const [activeStatus, setActiveStatus] = useState<ARCStatus | 'all'>('all');
  const [activeBook, setActiveBook] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  // Filter logic
  const filteredReaders = mockReaders.filter(reader => {
    const matchesStatus = activeStatus === 'all' || reader.status === activeStatus;
    const matchesBook = activeBook === 'all' || reader.bookTitle === activeBook;
    const matchesSearch = reader.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesBook && matchesSearch;
  });
  // Stats
  const stats = {
    total: mockReaders.length,
    sent: mockReaders.length,
    reviews: mockReaders.filter(r => r.status === 'reviewed').length,
    avgRating: 4.5,
    overdue: mockReaders.filter(r => r.isOverdue).length
  };
  return <div className={`min-h-screen pb-12 ${dk.page}`} data-id="element-17776" data-mp="3tjqecybbej4s">
      {/* Alert Banner */}
      {stats.overdue > 0 && <div className="bg-amber-50 border-b border-amber-200 px-4 py-3" data-id="element-17777" data-mp="2515zesll5d2">
          <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-17778" data-mp="bjcrvxivuv4e">
            <div className="flex items-center text-amber-800" data-id="element-17779" data-mp="bh8d2kjg4bpc">
              <AlertCircleIcon className="h-5 w-5 mr-2" data-id="element-17780" data-mp="7h80z6jtkclg" />
              <span className="text-sm font-medium" data-id="element-17781" data-mp="l94oexajlybi">
                {stats.overdue} ARC reader{stats.overdue > 1 ? 's' : ''} haven't
                started reading after 14+ days — consider sending a reminder.
              </span>
            </div>
            <button onClick={() => {
          setActiveStatus('overdue');
          setSearchQuery('');
        }} className="text-sm font-medium text-amber-900 underline hover:text-amber-700" data-id="element-17782" data-mp="3oqmtiwyxkr88">
              View Overdue
            </button>
          </div>
        </div>}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-17783" data-mp="1dy37bwmhp4hv">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8" data-id="element-17784" data-mp="1dzwinp81scw7">
          <div data-id="element-17785" data-mp="191csfcmf8ey1">
            <h1 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-17786" data-mp="33fgx3e3m58tn">
              ARC Management
            </h1>
            <p className={`mt-1 text-sm ${dk.textMuted}`} data-id="element-17787" data-mp="37gam6asskto4">
              Track advance reader copies and manage your review team
            </p>
          </div>
          <button className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500" data-id="element-17788" data-mp="3cdso3h8e68mm">
            <SendIcon className="h-4 w-4 mr-2" data-id="element-17789" data-mp="3r3gl469jil4j" />
            Send New Copies
          </button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-id="element-17790" data-mp="3v4k2wyb5er8e">
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-17791" data-mp="3zk0knkq881b">
            <div className="flex items-center justify-between" data-id="element-17792" data-mp="20tf3y7b0b2ze">
              <div data-id="element-17793" data-mp="25rgicexhme4j">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-17794" data-mp="25qnatbqpfklc">
                  Total Readers
                </p>
                <p className={`text-2xl font-bold mt-1 ${dk.textHeading}`} data-id="element-17795" data-mp="1thh7hjjoewq2">
                  {stats.total}
                </p>
              </div>
              <div className={`p-2 rounded-full ${dk.bgMuted}`} data-id="element-17796" data-mp="1oinrnkgx2tmq">
                <UsersIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-17797" data-mp="1jjjgwpecgnua" />
              </div>
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-17798" data-mp="2h5qo87218uac">
            <div className="flex items-center justify-between" data-id="element-17799" data-mp="2c772a6nfvihz">
              <div data-id="element-17800" data-mp="2c5vwk1afo0o8">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-17801" data-mp="278c7058ac1gz">
                  Copies Sent
                </p>
                <p className={`text-2xl font-bold mt-1 text-blue-600`} data-id="element-17802" data-mp="239d4era10gsp">
                  {stats.sent}
                </p>
              </div>
              <div className="p-2 rounded-full bg-blue-100" data-id="element-17803" data-mp="rrls7i3g1so8">
                <SendIcon className="h-5 w-5 text-blue-600" data-id="element-17804" data-mp="2qgjwa0lf3rw3" />
              </div>
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-17805" data-mp="2mf8h6p066p25">
            <div className="flex items-center justify-between" data-id="element-17806" data-mp="2md5ep5htz3gj">
              <div data-id="element-17807" data-mp="3075145ropsng">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-17808" data-mp="308g7mdcilei9">
                  Reviews Received
                </p>
                <p className={`text-2xl font-bold mt-1 text-amber-600`} data-id="element-17809" data-mp="3a2zrxo62au1b">
                  {stats.reviews}
                </p>
              </div>
              <div className="p-2 rounded-full bg-amber-100" data-id="element-17810" data-mp="3j4zaikwq666d">
                <StarIcon className="h-5 w-5 text-amber-600" data-id="element-17811" data-mp="3j4g0rrh8ao3f" />
              </div>
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-17812" data-mp="3e4bl23o59xe9">
            <div className="flex items-center justify-between" data-id="element-17813" data-mp="1orggvm5tf7qc">
              <div data-id="element-17814" data-mp="1jswvpnz1pzz1">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-17815" data-mp="1jtevgukx2wbp">
                  Avg Rating
                </p>
                <p className={`text-2xl font-bold mt-1 text-green-600`} data-id="element-17816" data-mp="49fgox6y6oku">
                  {stats.avgRating}★
                </p>
              </div>
              <div className="p-2 rounded-full bg-green-100" data-id="element-17817" data-mp="3ml2cv4aao9nw">
                <TrendingUpIcon className="h-5 w-5 text-green-600" data-id="element-17818" data-mp="3qnfk5x4owfms" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters Bar */}
        <div className={`rounded-lg shadow-sm border p-4 mb-6 ${dk.card} ${dk.border}`} data-id="element-17819" data-mp="3hnsjxenp5fvb">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between" data-id="element-17820" data-mp="33oss5y4w0kux">
            {/* Status Tabs */}
            <div className="w-full lg:w-auto overflow-x-auto pb-2 lg:pb-0 scrollbar-hide" data-id="element-17821" data-mp="37qecu5nny3fo">
              <div className="flex space-x-2" data-id="element-17822" data-mp="11y8zuqok7wox">
                {['all', 'sent', 'reading', 'reviewed', 'overdue', 'declined'].map(status => <button key={status} onClick={() => setActiveStatus(status as ARCStatus | 'all')} className={`px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${activeStatus === status ? 'bg-purple-100 text-purple-700' : `${dk.textMuted} hover:${dk.bgMuted}`}`} data-id="element-17823" data-mp="29w3n93l5vdvu">
                    {status === 'all' ? 'All Statuses' : status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>)}
              </div>
            </div>

            {/* Right Side: Book & Search */}
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto" data-id="element-17824" data-mp="2jsgelut8hfnf">
              <div className="relative min-w-[200px]" data-id="element-17825" data-mp="3hbhsxm56a02h">
                <select value={activeBook} onChange={e => setActiveBook(e.target.value)} className={`w-full pl-3 pr-8 py-2 text-sm border rounded-md appearance-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 ${dk.input} ${dk.border}`} data-id="element-17826" data-mp="3cioq53t9l1t1">
                  <option value="all" data-id="element-17827" data-mp="3trr9xuq6f9kr">All Books</option>
                  <option value="The Silent Echo" data-id="element-17828" data-mp="1ors5drmjuk4">The Silent Echo</option>
                  <option value="Whispers in the Dark" data-id="element-17829" data-mp="1mncxzo9z94j">
                    Whispers in the Dark
                  </option>
                  <option value="Beyond the Veil" data-id="element-17830" data-mp="bmxrbnm1qmla">Beyond the Veil</option>
                </select>
                <ChevronDownIcon className={`absolute right-2.5 top-2.5 h-4 w-4 pointer-events-none ${dk.textMuted}`} data-id="element-17831" data-mp="gdnuqvawwfdc" />
              </div>

              <div className="relative flex-grow sm:max-w-xs" data-id="element-17832" data-mp="letqz33s6krm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-17833" data-mp="pfdmy3cmbic6">
                  <SearchIcon className={`h-4 w-4 ${dk.textMuted}`} data-id="element-17834" data-mp="ue7363jahng1" />
                </div>
                <input type="text" placeholder="Search readers..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`block w-full pl-10 pr-3 py-2 border rounded-md leading-5 text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 ${dk.input} ${dk.border}`} data-id="element-17835" data-mp="gpn6my9hdfja" />
              </div>
            </div>
          </div>
        </div>

        {/* Empty State */}
        {filteredReaders.length === 0 ? <div className={`text-center py-12 rounded-lg border border-dashed ${dk.border} ${dk.bgMuted}/30`} data-id="element-17836" data-mp="7qjphztvja7f">
            <SearchIcon className={`mx-auto h-12 w-12 ${dk.textMuted}`} data-id="element-17837" data-mp="2pcfdu49dyxl" />
            <h3 className={`mt-2 text-sm font-medium ${dk.textHeading}`} data-id="element-17838" data-mp="1j4dwlrapr7b0">
              No ARC readers found
            </h3>
            <p className={`mt-1 text-sm ${dk.textMuted}`} data-id="element-17839" data-mp="1e5kjhzzc9ibd">
              Try adjusting your search or filters.
            </p>
            <div className="mt-6" data-id="element-17840" data-mp="2w6222anqghai">
              <button onClick={() => {
            setActiveBook('all');
            setActiveStatus('all');
            setSearchQuery('');
          }} className="text-sm font-medium text-purple-600 hover:text-purple-500" data-id="element-17841" data-mp="313284lkl1k1m">
                Clear all filters
              </button>
            </div>
          </div> : <>
            {/* Desktop Table View */}
            <div className={`hidden md:block rounded-lg shadow-sm border overflow-hidden ${dk.card} ${dk.border}`} data-id="element-17842" data-mp="3jve7r0juq782">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-17843" data-mp="3b0g9rf1uk49v">
                <thead className={dk.bgMuted} data-id="element-17844" data-mp="2lg2c7ty5ue4">
                  <tr data-id="element-17845" data-mp="2jlg8q6xvw8g">
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-17846" data-mp="2jbs785gfz5e">
                      Reader
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-17847" data-mp="c9tvi8x44f71">
                      Book
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-17848" data-mp="2cyu9i9gg0p2">
                      Status
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-17849" data-mp="3kpn9ag027cke">
                      Date Sent
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-17850" data-mp="36osyjqpjgzik">
                      Due Date
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-17851" data-mp="1q72ayx8yy50w">
                      Rating
                    </th>
                    <th scope="col" className={`relative px-6 py-3`} data-id="element-17852" data-mp="12istg7isg3fk">
                      <span className="sr-only" data-id="element-17853" data-mp="12gpwf3j7xa1c">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y divide-gray-200 dark:divide-gray-700 ${dk.card}`} data-id="element-17854" data-mp="1ch8pmowoa2vx">
                  {filteredReaders.map(reader => <tr key={reader.id} className={`hover:${dk.bgMuted}/50 transition-colors`} data-id="element-17855" data-mp="1h9i7rirjpynn">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-17856" data-mp="opthgfi7srcr">
                        <div className="flex items-center" data-id="element-17857" data-mp="3e8ocnr0xlmkz">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-medium text-sm ${reader.avatarColor}`} data-id="element-17858" data-mp="3ebav4lpd1mhz">
                            {reader.initials}
                          </div>
                          <div className="ml-4" data-id="element-17859" data-mp="3id5uhknqdpt4">
                            <div className={`text-sm font-medium ${dk.textHeading}`} data-id="element-17860" data-mp="124ry29wueat">
                              {reader.name}
                            </div>
                            <div className={`text-xs ${dk.textMuted}`} data-id="element-17861" data-mp="9tydk34aqf79">
                              {reader.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-17862" data-mp="jr2yjzmeo4mh">
                        <div className="flex items-center" data-id="element-17863" data-mp="evw3fg77c896">
                          <img className="h-10 w-10 rounded object-cover mr-3" src={reader.bookCover} alt="" data-id="element-17864" data-mp="yfl87qqhbk5m" />
                          <div className={`text-sm ${dk.textHeading}`} data-id="element-17865" data-mp="ogx31sjlezjc">
                            {reader.bookTitle}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-17866" data-mp="fov3dwhclna4">
                        <StatusBadge status={reader.status} data-id="element-17867" data-mp="kgvac8vu5gg7" />
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${dk.textSecondary}`} data-id="element-17868" data-mp="5rpaqjt35o7s">
                        {reader.dateSent}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-17869" data-mp="3svn43lzsggzp">
                        <div className={`text-sm ${dk.textSecondary}`} data-id="element-17870" data-mp="3syj21abmz5o5">
                          {reader.dueDate}
                        </div>
                        {reader.isOverdue && <div className="text-xs text-red-500 font-medium mt-0.5" data-id="element-17871" data-mp="2n73txd2mmytw">
                            Overdue!
                          </div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-17872" data-mp="2e6fgajh5b0hj">
                        {reader.rating ? <div className="flex text-amber-400" data-id="element-17873" data-mp="35vik5y54e8tn">
                            {[...Array(reader.rating)].map((_, i) => <StarIcon key={i} className="h-4 w-4 fill-current" data-id="element-17874" data-mp="3atadmwley2zy" data-mpi={i} />)}
                          </div> : <span className={`text-sm ${dk.textMuted}`} data-id="element-17875" data-mp="3oucwl0h42x6n">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-17876" data-mp="3tluxxpn1qzs5">
                        <div className="flex items-center justify-end space-x-3" data-id="element-17877" data-mp="1i4sx9307f5dn">
                          {(reader.status === 'sent' || reader.status === 'reading' || reader.status === 'overdue') && <button className={`text-gray-400 hover:${dk.textHeading}`} title="Send Reminder" data-id="element-17878" data-mp="1mwue728bhka0">
                              <BellIcon className="h-4 w-4" data-id="element-17879" data-mp="u858sr08assg" />
                            </button>}
                          {reader.status === 'reviewed' && <button className={`text-purple-600 hover:text-purple-900`} title="View Review" data-id="element-17880" data-mp="1462w9ca9klk0">
                              <EyeIcon className="h-4 w-4" data-id="element-17881" data-mp="2fl8ejomvosoq" />
                            </button>}
                          <button className={`text-gray-400 hover:text-red-500`} title="Remove" data-id="element-17882" data-mp="2moy4d062aewv">
                            <TrashIcon className="h-4 w-4" data-id="element-17883" data-mp="2wmm8ggpk4yrp" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4" data-id="element-17884" data-mp="28x1ez3vs2j46">
              {filteredReaders.map(reader => <div key={reader.id} className={`relative rounded-lg shadow-sm border p-4 ${dk.card} ${dk.border} ${reader.isOverdue ? 'border-l-4 border-l-red-500' : ''}`} data-id="element-17885" data-mp="2dw4lytvbizes">
                  <div className="flex gap-4" data-id="element-17886" data-mp="1l88p9p5r17i0">
                    <div className="flex-shrink-0" data-id="element-17887" data-mp="1l5crc0twiitk">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-medium text-sm ${reader.avatarColor}`} data-id="element-17888" data-mp="1q5qtj28tmyjo">
                        {reader.initials}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-17889" data-mp="3ufc7e48v8piy">
                      <div className="flex justify-between items-start" data-id="element-17890" data-mp="3ui83ro52f65a">
                        <div data-id="element-17891" data-mp="3pgiqs2ya8iek">
                          <h3 className={`text-sm font-medium truncate pr-2 ${dk.textHeading}`} data-id="element-17892" data-mp="3pjeobq67x52h">
                            {reader.name}
                          </h3>
                          <div className={`text-xs ${dk.textMuted} mb-1`} data-id="element-17893" data-mp="3bp485x8aej81">
                            {reader.bookTitle}
                          </div>
                        </div>
                        <div className="flex items-center" data-id="element-17894" data-mp="31rgb30n7kzmp">
                          <button className={`p-1 rounded-full hover:${dk.bgMuted} ${dk.textMuted}`} data-id="element-17895" data-mp="31qy9rplovv7x">
                            <MoreVerticalIcon className="h-4 w-4" data-id="element-17896" data-mp="10ooympe13ytb" />
                          </button>
                        </div>
                      </div>

                      <div className="mt-2 flex flex-wrap gap-2 items-center" data-id="element-17897" data-mp="15qe840lls4fa">
                        <StatusBadge status={reader.status} data-id="element-17898" data-mp="qyvvmcrycs0o" />
                        <span className={`text-xs ${dk.textSecondary}`} data-id="element-17899" data-mp="vy08pehzz9wa">
                          {reader.dateSent}
                        </span>
                      </div>

                      {reader.rating && <div className="mt-2 flex text-amber-400" data-id="element-17900" data-mp="mylgoam6mr9o">
                          {[...Array(reader.rating)].map((_, i) => <StarIcon key={i} className="h-3 w-3 fill-current" data-id="element-17901" data-mp="82xgqxnwcqbl" data-mpi={i} />)}
                        </div>}

                      <div className="mt-3 flex items-center justify-between border-t pt-3 dark:border-gray-700" data-id="element-17902" data-mp="27cm37tia5yf2">
                        <div className={`text-xs ${dk.textMuted}`} data-id="element-17903" data-mp="3rwhi3f1ttdg7">
                          Due:{' '}
                          <span className={reader.isOverdue ? 'text-red-500 font-medium' : ''} data-id="element-17904" data-mp="3my7kkxsmq0rf">
                            {reader.dueDate}
                          </span>
                        </div>

                        {reader.status === 'sent' || reader.status === 'reading' || reader.status === 'overdue' ? <button className="text-xs font-medium text-purple-600 hover:text-purple-500 flex items-center" data-id="element-17905" data-mp="odxuipu3evrq">
                            <BellIcon className="h-3 w-3 mr-1" data-id="element-17906" data-mp="jgpccp8y2rqh" />
                            Remind
                          </button> : reader.status === 'reviewed' ? <button className="text-xs font-medium text-purple-600 hover:text-purple-500 flex items-center" data-id="element-17907" data-mp="2l93t3kzr5q5b">
                            <EyeIcon className="h-3 w-3 mr-1" data-id="element-17908" data-mp="p6h97y4r9aii" />
                            View Review
                          </button> : null}
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>
          </>}
      </div>
    </div>;
};
export default ARCManagement;